import React, { useEffect, useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { useHotkeys } from 'react-hotkeys-hook';
import { enqueueSnackbar } from 'notistack';

import { postCreateGroupWithOrganizations } from '../../../api/groupsApi';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { getAllChildOrganizations } from '../../../api';
import { useAppSelector } from '../../../store/hooks';
import styles from './CreateGroupModal.module.scss';
import { ORGANIZATION } from '../../../enums/TYPES';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';

type TCreateOrgGroupModal = {
  open: boolean;
  switchModal: () => void;
  setFetchTrigger: (num: number) => void;
};

const CreateOrgGroupModal: React.FC<TCreateOrgGroupModal> = ({ open, switchModal, setFetchTrigger }) => {
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(50);
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const { myOrganization } = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState(false);

  const [allChildOrgs, setAllChildOrgs] = useState<ORGANIZATION[]>([]);

  const fetchAllChildOrgs = async () => {
    try {
      setLoading(true);
      const res = await getAllChildOrganizations({ id: +myOrganization.id, page: 0, limit: 1000, search: '' });
      if (res.data.documents.length) {
        setAllChildOrgs(res.data.documents);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && myOrganization?.id) {
      fetchAllChildOrgs();
    }
  }, [open, myOrganization]);

  const onSubmit = async () => {
    try {
      setLoading(true);
      const res = await postCreateGroupWithOrganizations({
        title: groupTitle,
        analysts: [],
        viewers: [],
        organizations: selectedOrgIds,
      });
      setFetchTrigger(Date.now());
      onClose();
    } catch (err: any) {
      console.log(err);
      if (err?.response?.data?.message) {
        enqueueSnackbar(err.response.data.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const [orgSearch, setOrgSearch] = useState('');

  const stepBack = () => {
    setStep(1);
    setProgress(50);
  };

  const nextStep = (e: any) => {
    e.preventDefault();
    setStep(2);
    setProgress(100);
  };

  const [groupTitle, setGroupTitle] = useState('');

  const onClose = () => {
    switchModal();
    setStep(1);
    setProgress(50);
    setGroupTitle('');
    setOrgSearch('');
    setSelectedOrgIds([]);
  };

  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);

  const handleAddCheckbox = (id: number) => {
    if (selectedOrgIds.find((orgId) => orgId == id)) {
      const arr = selectedOrgIds.filter((orgId) => orgId != id);
      setSelectedOrgIds(arr);
    } else {
      setSelectedOrgIds([...selectedOrgIds, id]);
    }
  };

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          {step === 1 && <div className={styles.title}>Create a group</div>}
          {step === 2 && <div className={styles.title}>Assign Child Organisations</div>}
          <div className={styles.step}>Step {step}/2</div>
        </div>
        <LinearProgress className={styles.progressBar} variant="determinate" value={progress} />
        {step === 1 && (
          <>
            <form onSubmit={nextStep} className={styles.titleContainer}>
              <div className={styles.subtitle}>Enter Group Name</div>
              <input
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                required
                className={styles.field}
                placeholder={'Group Name'}
              />
              <ContainedButton type="submit" className={styles.button}>
                <div ref={toSecondStepButtonRef}>Next</div>
              </ContainedButton>
              <OutlinedButton className={styles.cancel} kind="default" type="button" onClick={onClose}>
                Cancel
              </OutlinedButton>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <input
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              className={styles.search}
              placeholder={'Search Group'}
            />
            <div className={styles.orgList}>
              {allChildOrgs
                .filter((org) => org.name.toLocaleLowerCase().includes(orgSearch.toLocaleLowerCase()))
                .map((org) => (
                  <div key={org.id} className={styles.orgLine}>
                    <Avatar avatar={org.image_url} />
                    <div className={styles.orgInfo}>
                      <div className={styles.name}> {org.name}</div>
                      <div className={styles.location}> {org.location}</div>
                    </div>

                    <div className={styles.checkboxWrapper30}>
                      <span className={styles.checkbox}>
                        <input
                          type="checkbox"
                          onChange={() => handleAddCheckbox(+org.id)}
                          checked={!!selectedOrgIds.find((orgId) => +orgId == +org.id)}
                        />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                    </div>
                  </div>
                ))}
            </div>
            <ContainedButton onClick={onSubmit} disabled={!selectedOrgIds.length || loading} className={styles.button}>
              <div ref={submitButtonRef}>Save</div>
            </ContainedButton>
            <OutlinedButton disabled={loading} className={styles.cancel} kind="default" type="button" onClick={onClose}>
              Cancel
            </OutlinedButton>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CreateOrgGroupModal;
